import React from 'react'

import { cn } from '@/lib/cn'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../card'

type IdeaCardProps = {
  variant?: string
}

const IdeaCard = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Card> & IdeaCardProps
>(({ className, variant, ...props }, ref) => {
  return (
    <Card
      ref={ref}
      className={cn(
        'flex w-full md:min-w-96 cursor-pointer flex-col justify-between rounded-lg border border-system-border-secondary px-3 py-4 shadow-sm',
        className,
      )}
      style={{
        borderTop: `4px solid ${variant || '#0073EA'}`,
      }}
      {...props}
    />
  )
})
IdeaCard.displayName = 'IdeaCard'

const IdeaCardHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof CardHeader>
>(({ className, ...props }, ref) => (
  <CardHeader
    ref={ref}
    className={cn('flex flex-col p-0 gap-4', className)}
    {...props}
  />
))
IdeaCardHeader.displayName = 'IdeaCardHeader'

const IdeaCardSequence = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'font-inter text-xs font-medium text-system-text-secondary',
      className,
    )}
    {...props}
  />
))
IdeaCardSequence.displayName = 'IdeaCardSequence'

const IdeaCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof CardTitle>
>(({ className, ...props }, ref) => (
  <CardTitle
    ref={ref}
    className={cn(
      'font-inter text-base font-semibold text-system-text-primary',
      className,
    )}
    {...props}
  />
))
IdeaCardTitle.displayName = 'IdeaCardTitle'

const IdeaCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof CardDescription>
>(({ className, ...props }, ref) => (
  <CardDescription
    ref={ref}
    className={cn(
      'line-clamp-2 text-sm font-normal text-system-text-secondary',
      className,
    )}
    {...props}
  />
))
IdeaCardDescription.displayName = 'IdeaCardDescription'

const IdeaCardContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof CardContent>
>(({ className, ...props }, ref) => (
  <CardContent
    ref={ref}
    className={cn('gap-5 flex flex-col', className)}
    {...props}
  />
))
IdeaCardContent.displayName = 'IdeaCardContent'

const IdeaCardBadge = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center gap-1 text-system-text-primary text-xs font-medium [&>svg]:size-4 [&>svg]:text-system-primary-icon border border-system-grey-strong rounded-sm px-4 py-2',
      className,
    )}
    {...props}
  />
))

IdeaCardBadge.displayName = 'IdeaCardBadge'

const IdeaCardParticipants = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
    <span className="text-xs font-medium text-system-text-secondary">
      Participantes
    </span>
    {children}
  </div>
))
IdeaCardParticipants.displayName = 'IdeaCardParticipants'

type BadgeProps = React.HTMLAttributes<HTMLDivElement> & {
  withPoint?: boolean
  color?: string
}

const BadgeStatus = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & BadgeProps
>(
  (
    { className, children, withPoint = true, color = '#333', ...props },
    ref,
  ) => {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-2xl border px-2 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          className,
        )}
        style={{
          borderColor: color,
          backgroundColor: 'transparent',
          color,
        }}
        {...props}
        ref={ref}
      >
        {withPoint && (
          <div
            className="size-2 rounded-full"
            style={{ backgroundColor: color }}
          ></div>
        )}
        {children}
      </div>
    )
  },
)

BadgeStatus.displayName = 'BadgeStatus'

const IdeaCardStatus = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    color?: string
  }
>(({ className, children, color, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
    <span className="text-xs font-medium text-system-text-secondary">
      Status
    </span>
    <BadgeStatus color={color} withPoint>
      {children}
    </BadgeStatus>
  </div>
))
IdeaCardStatus.displayName = 'IdeaCardStatus'

const IdeaCardSimilarity = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
    <span className="text-xs font-medium text-system-text-secondary">
      Similar
    </span>
    {children}
  </div>
))
IdeaCardSimilarity.displayName = 'IdeaCardSimilarity'

const IdeaCardChallenge = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    sequence: string
    title: string
  }
>(({ className, sequence, title, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('text-sm text-system-text-primary', className)}
    {...props}
  >
    <strong>Direcional {sequence}:</strong> {title}
  </span>
))
IdeaCardChallenge.displayName = 'IdeaCardChallenge'

const IdeaCardWorkflow = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
      <span className="text-xs font-medium text-system-content-american-gray">
        Fluxo de trabalho
      </span>
      {children}
    </div>
  )
})
IdeaCardWorkflow.displayName = 'IdeaCardWorkflow'

export {
  IdeaCard,
  IdeaCardHeader,
  IdeaCardSequence,
  IdeaCardTitle,
  IdeaCardDescription,
  IdeaCardContent,
  IdeaCardBadge,
  IdeaCardParticipants,
  IdeaCardStatus,
  IdeaCardChallenge,
  IdeaCardWorkflow,
  IdeaCardSimilarity,
}
